/**
 * API client for Centre Operations → Reports.
 *
 * Backend: oh_operations/centre_ops_report/alfred, one segment per call
 * (checklist | interactions | parent) so each part of the page can draw as its
 * numbers arrive. `apiEndpoints` is read INSIDE the function (TDZ-safety —
 * mirrors checklist.js).
 */
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl } from '../app/helpers'
import { get, post } from '../request'

const fetchCentreOpsReport = async ({ segment, from, to } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.centreOpsReport}/alfred`, {
        segment,
        from,
        to
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching centre ops report')
    throw error
  }
}

// The list behind one Preschool Ops tile for one centre:
// {item, title, note, summary, columns, rows, filter}
const fetchCentreOpsDetail = async ({ item, centre, from, to } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.centreOpsReport}/detail/alfred`, {
        item,
        centre,
        from,
        to
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching centre ops detail')
    throw error
  }
}

// One row of a Preschool Ops list in full (an incident, an observation).
const fetchCentreOpsRecord = async ({ kind, id, centre } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.centreOpsReport}/record/alfred`, {
        kind,
        id,
        centre
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching centre ops record')
    throw error
  }
}

// CD Week Plan: {week_start, week_end, basis_from, basis_to, actions}.
// week = any day of the week; none = the week to plan.
const fetchCdWeekPlan = async ({ centre, week } = {}) => {
  try {
    const response = await get(
      makeRequestUrl(`${apiEndpoints.centreOpsReport}/week_plan/alfred`, {
        centre,
        week
      })
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching CD week plan')
    throw error
  }
}

// Week Plan → Log action: {item_key, title, note, by, at}. Refused (409)
// where the server doesn't save logs (a local setup on the live database).
const saveCdWeekLog = async ({ centre, week, itemKey, title, note } = {}) => {
  const response = await post(
    `${apiEndpoints.centreOpsReport}/week_plan/log/alfred`,
    { centre, week, item_key: itemKey, title, note }
  )
  return response.data
}

// Week Plan: save the week's plan once it is ready; the first save wins
// and a later one gets that plan back. Refused (409) where the server
// doesn't save (a local setup on the live database).
const saveCdWeekPlan = async ({ centre, week, plan } = {}) => {
  const response = await post(
    `${apiEndpoints.centreOpsReport}/week_plan/save/alfred`,
    { centre, week, plan }
  )
  return response.data
}

export {
  saveCdWeekPlan,
  saveCdWeekLog,
  fetchCentreOpsReport,
  fetchCentreOpsDetail,
  fetchCentreOpsRecord,
  fetchCdWeekPlan
}
